/** Choices offered by the node inspector. Values are persisted; labels are display-only. */

export const registerOptions = [
  { value: 'R1', label: 'R1' },
  { value: 'R2', label: 'R2' },
  { value: 'R3', label: 'R3' },
]

export const registerOrLiteralOptions = [
  ...registerOptions,
  { value: 'literal', label: 'Literal value' },
]

export const condOpOptions = [
  { value: '==', label: '== (equal)' },
  { value: '!=', label: '!= (not equal)' },
  { value: '<', label: '< (less than)' },
  { value: '<=', label: '<= (less or equal)' },
  { value: '>', label: '> (greater than)' },
  { value: '>=', label: '>= (greater or equal)' },
]

export const getSourceOptions = [
  { value: 'group-size', label: 'Group size' },
  { value: 'deck-size', label: 'Deck size' },
  { value: 'group-pool-remaining', label: 'Group pool remaining' },
  { value: 'deck-pool-remaining', label: 'Deck pool remaining' },
  { value: 'group-done', label: 'Group done' },
  { value: 'deck-done', label: 'Deck done' },
  { value: 'all-checkboxes-done', label: 'All group checkboxes ticked? (0/1)' },
  { value: 'group-max-checkbox-ticked', label: 'Group: max ticked checkboxes (card)' },
  { value: 'group-max-checkbox-total', label: 'Group: max total checkboxes (card)' },
  { value: 'session-known', label: 'This level: cards answered "known"' },
  { value: 'session-answered', label: 'This level: cards answered' },
  { value: 'phase-known', label: 'Phase: cards answered "known" (all levels)' },
  { value: 'phase-answered', label: 'Phase: cards answered (all levels)' },
  { value: 'literal', label: 'Literal value' },
]

/** Sources that read a phase's stored answers (course levels only). */
export const PHASE_GET_SOURCES = ['phase-known', 'phase-answered']

export const setTargetOptions = [
  { value: 'progress-total', label: 'Progress bar — total (denominator)' },
  { value: 'progress-current', label: 'Progress bar — current (numerator)' },
]

export const mathOpOptions = [
  { value: '+1', label: '+1 (increment by 1)' },
  { value: '-1', label: '-1 (decrement by 1)' },
  { value: '+n', label: '+N (increment by literal)' },
  { value: '-n', label: '-N (decrement by literal)' },
  { value: 'add', label: '+ add operand (register or literal)' },
  { value: 'sub', label: '− subtract operand' },
  { value: 'mul', label: '× multiply by operand' },
  { value: 'set', label: '= set to operand' },
]

/** Labels used by node summaries for actions without a dedicated summary. */
export const actionOptions = [
  { value: 'pull-next-card', label: 'Pull card' },
  { value: 'pull-next-card-back-first', label: 'Pull next card (back first)' },
  { value: 'set-initial-checkboxes', label: 'Set initial checkboxes' },
  { value: 'click-checkbox', label: 'Click checkbox (tick N checkboxes)' },
  { value: 'unclick-checkbox', label: 'Unclick checkbox (untick N checkboxes)' },
  { value: 'rotate-card', label: 'Rotate card (flip front/back)' },
  { value: 'wait-for-user-action', label: 'Wait for user click' },
  { value: 'ask-known', label: 'Ask: known? (✓ / ✗ buttons)' },
  { value: 'offer-practice', label: 'Offer practice round' },
  { value: 'delay', label: 'Delay (pause before continuing)' },
  { value: 'get-value', label: 'Get value → register' },
  { value: 'set-value', label: 'Set value ← register' },
  { value: 'math-op', label: 'Math operation on register' },
  { value: 'emit-complete', label: 'Emit: flow complete (early exit)' },
  { value: 'show-media', label: 'Show Media (course media)' },
]

/** Action picker shown for a generic action node that has no action type yet. */
export const groupedActionOptions = [
  {
    label: 'Cards',
    options: [
      { value: 'pull-next-card', label: 'Pull card' },
      { value: 'set-initial-checkboxes', label: 'Set initial checkboxes' },
      { value: 'click-checkbox', label: 'Click checkbox (tick N)' },
      { value: 'unclick-checkbox', label: 'Unclick checkbox (untick N)' },
      { value: 'rotate-card', label: 'Flip card (rotate front/back)' },
      { value: 'ask-known', label: 'Ask: known? (✓ / ✗ buttons)' },
    ],
  },
  {
    label: 'Flow',
    options: [
      { value: 'wait-for-user-action', label: 'Wait for user click' },
      { value: 'delay', label: 'Delay (pause before continuing)' },
      { value: 'emit-complete', label: 'Early exit (complete flow)' },
      { value: 'offer-practice', label: 'Offer practice round' },
    ],
  },
  {
    label: 'Registers',
    options: [
      { value: 'get-value', label: 'Get value → register' },
      { value: 'set-value', label: 'Set value ← register' },
      { value: 'math-op', label: 'Math operation on register' },
    ],
  },
  {
    label: 'Media',
    options: [{ value: 'show-media', label: 'Show Media' }],
  },
]

export const mediaTypeChoices = [
  { value: 'image', icon: '🖼', label: 'Bild' },
  { value: 'video', icon: '▶', label: 'Video' },
  { value: 'audio', icon: '🎵', label: 'Audio' },
  { value: 'pdf', icon: '📄', label: 'PDF' },
]

/** Legacy per-type media actions are edited like 'show-media'. */
export const MEDIA_ACTION_TYPES = [
  'show-media',
  'show-image',
  'show-video',
  'show-audio',
  'show-pdf',
]

export const isMediaAction = (actionType: string | undefined) =>
  MEDIA_ACTION_TYPES.includes(actionType ?? '')
